import { PriceList, Stake, Token } from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { StakeFunc, StakeProps } from '../types'
import { safeGasEstimates } from '../utils'

const VaultAbi = [
  {
    inputs: [
      { internalType: 'address', name: '_merlin', type: 'address' },
      { internalType: 'address', name: '_router', type: 'address' },
      { internalType: 'address', name: '_priceCalc', type: 'address' },
      { internalType: 'address', name: '_merlinBNB', type: 'address' },
      { internalType: 'address', name: '_wbnb', type: 'address' },
      { internalType: 'address', name: '_zapBSC', type: 'address' },
      { internalType: 'address', name: '_btcb', type: 'address' },
      { internalType: 'address', name: '_eth', type: 'address' }
    ],
    stateMutability: 'nonpayable',
    type: 'constructor'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Deposited',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'uint256', name: 'profit', type: 'uint256' }],
    name: 'Harvested',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'profit', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'performanceFee', type: 'uint256' }
    ],
    name: 'MerlinPaid',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'previousOwner', type: 'address' },
      { indexed: true, internalType: 'address', name: 'newOwner', type: 'address' }
    ],
    name: 'OwnershipTransferred',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'bool', name: 'isPaused', type: 'bool' }],
    name: 'PauseChanged',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'profit', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'performanceFee', type: 'uint256' }
    ],
    name: 'ProfitPaid',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: 'address', name: 'token', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Recovered',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'uint256', name: 'reward', type: 'uint256' }],
    name: 'RewardAdded',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'reward', type: 'uint256' }
    ],
    name: 'RewardPaid',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'uint256', name: 'newDuration', type: 'uint256' }],
    name: 'RewardsDurationUpdated',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Staked',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' }
    ],
    name: 'Withdrawn',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'user', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'amount', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'withdrawalFee', type: 'uint256' }
    ],
    name: 'Withdrawn',
    type: 'event'
  },
  {
    inputs: [],
    name: 'BTCB',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'ETH',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'WBNB',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: '_rewardsToken',
    outputs: [{ internalType: 'contract IBEP20', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: '_stakingToken',
    outputs: [{ internalType: 'contract IBEP20', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'apy',
    outputs: [
      { internalType: 'uint256', name: '_usd', type: 'uint256' },
      { internalType: 'uint256', name: '_merlin', type: 'uint256' },
      { internalType: 'uint256', name: '_bnb', type: 'uint256' }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'balance',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    name: 'deposit',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  { inputs: [], name: 'depositAll', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'depositedAt',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'earned',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  { inputs: [], name: 'getReward', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [],
    name: 'getRewardForDuration',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  { inputs: [], name: 'getRewardInBTC', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  { inputs: [], name: 'getRewardInETH', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  { inputs: [], name: 'harvest', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'info',
    outputs: [
      {
        components: [
          { internalType: 'uint256', name: 'balance', type: 'uint256' },
          { internalType: 'uint256', name: 'principal', type: 'uint256' },
          { internalType: 'uint256', name: 'available', type: 'uint256' },
          {
            components: [
              { internalType: 'uint256', name: 'usd', type: 'uint256' },
              { internalType: 'uint256', name: 'merlin', type: 'uint256' },
              { internalType: 'uint256', name: 'bnb', type: 'uint256' }
            ],
            internalType: 'struct Profit',
            name: 'profit',
            type: 'tuple'
          },
          { internalType: 'uint256', name: 'poolTVL', type: 'uint256' },
          {
            components: [
              { internalType: 'uint256', name: 'usd', type: 'uint256' },
              { internalType: 'uint256', name: 'merlin', type: 'uint256' },
              { internalType: 'uint256', name: 'bnb', type: 'uint256' }
            ],
            internalType: 'struct APY',
            name: 'poolAPY',
            type: 'tuple'
          }
        ],
        internalType: 'struct UserInfo',
        name: '',
        type: 'tuple'
      }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'lastPauseTime',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'lastTimeRewardApplicable',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'lastUpdateTime',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'merlinBNB',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'minter',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'uint256', name: 'reward', type: 'uint256' }],
    name: 'notifyRewardAmount',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [],
    name: 'owner',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'paused',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'periodFinish',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'poolType',
    outputs: [{ internalType: 'enum PoolConstant.PoolTypes', name: '', type: 'uint8' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'priceShare',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'principalOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'profitOf',
    outputs: [
      { internalType: 'uint256', name: '_usd', type: 'uint256' },
      { internalType: 'uint256', name: '_merlin', type: 'uint256' },
      { internalType: 'uint256', name: '_bnb', type: 'uint256' }
    ],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [
      { internalType: 'address', name: 'tokenAddress', type: 'address' },
      { internalType: 'uint256', name: 'tokenAmount', type: 'uint256' }
    ],
    name: 'recoverBEP20',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  { inputs: [], name: 'renounceOwnership', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [],
    name: 'rewardPerToken',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'rewardPerTokenStored',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'rewardRate',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'rewards',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'rewardsDistribution',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'rewardsDuration',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'rewardsToken',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'bool', name: '_paused', type: 'bool' }],
    name: 'setPaused',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: '_rewardsDistribution', type: 'address' }],
    name: 'setRewardsDistribution',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'uint256', name: '_rewardsDuration', type: 'uint256' }],
    name: 'setRewardsDuration',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: '__rewardsToken', type: 'address' }],
    name: 'setRewardsToken',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [
      { internalType: 'address', name: '_address', type: 'address' },
      { internalType: 'bool', name: 'permission', type: 'bool' }
    ],
    name: 'setStakePermission',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'sharesOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
      { internalType: 'address', name: '_to', type: 'address' }
    ],
    name: 'stakeTo',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [],
    name: 'stakingToken',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'totalSupply',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [],
    name: 'tvl',
    outputs: [{ internalType: 'uint256', name: 'valueInBNB', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: '', type: 'address' }],
    name: 'userRewardPerTokenPaid',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'uint256', name: 'amount', type: 'uint256' }],
    name: 'withdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  { inputs: [], name: 'withdrawAll', outputs: [], stateMutability: 'nonpayable', type: 'function' },
  {
    inputs: [{ internalType: 'uint256', name: '_amount', type: 'uint256' }],
    name: 'withdrawUnderlying',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    inputs: [{ internalType: 'address', name: 'account', type: 'address' }],
    name: 'withdrawableBalanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  },
  {
    inputs: [],
    name: 'zapBSC',
    outputs: [{ internalType: 'contract IZapBSC', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function'
  }
]

type PoolType = 'TOKEN' | 'LP'

export interface FarmConfig {
  pid: number
  lpSymbol: string
  pool: PoolType
  lpAddresses: Address
  tokenSymbol: string
  tokenAddresses: Address
  quoteTokenAdresses: Address
  multiplier?: string
  isCommunity?: boolean
  earn: string
  dual?: {
    rewardPerBlock: number
    earnLabel: string
    endBlock: number
  }
}

interface Address {
  97?: string
  56: string
}

const wbnb = {
  97: '0xae13d989dac2f0debff460ac112a837c89baa7cd',
  56: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c'
}

const farms: FarmConfig[] = [
  {
    pid: 0,
    lpSymbol: 'MERL Promo',
    pool: 'TOKEN',
    lpAddresses: {
      56: '0xfF1Da0430C3C4afd6005c511cd5E271BE04f4738'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'WBNB',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 1,
    lpSymbol: 'MERL',
    pool: 'TOKEN',
    lpAddresses: {
      56: '0x3b87475ac293eeed0e8bC25713Eb8242A9497C3F'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'WBNB + ETH + BTCB',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 2,
    lpSymbol: 'CAKE',
    pool: 'TOKEN',
    lpAddresses: {
      56: '0xa69AC402a8Afe80C4Db293baF231242242A131Af'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 3,
    lpSymbol: 'CAKE-WBNB LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x4E5f611A964806c5fb79Aa0dC8cf945cDFdaa7E2'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'CAKE-WBNB LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 4,
    lpSymbol: 'CAKE-WBNB LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x5bb11D23c94B13FbA6D22eAbb83bF3fd2458763E'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 5,
    lpSymbol: 'WBNB-BUSD LP',
    pool: 'LP',
    lpAddresses: {
      56: '0xBf3aa34aC3F6ea54DD0eC15e5011e0B5A09c17f1'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'WBNB-BUSD LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 6,
    lpSymbol: 'WBNB-BUSD LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x8AFdA0Df3D9601B32B731f829060BC0C1C95270D'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 7,
    lpSymbol: 'WBNB-ETH LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x925697B5E8F9b1277ca9d94769E10C4cBf1676f5'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xda360309c59cb8c434b28a91b823344a96444278'
    },
    earn: 'WBNB-ETH LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 8,
    lpSymbol: 'WBNB-ETH LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 9,
    lpSymbol: 'WBNB-BTCB LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x82089743e90562D001FECCE2EeF465a609829459'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'WBNB-BTCB LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 10,
    lpSymbol: 'WBNB-BTCB LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0xF02ef3DcD71F1881f7d1F6Fc6D6f95c021d874b1'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 11,
    lpSymbol: 'WBNB-USDT LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x4675D999c1896ADD7D839F090703269b721eD411'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'WBNB-USDT LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 12,
    lpSymbol: 'WBNB-USDT LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x414C51d5e837514Ac133cC9C8E14516ddBCc09c1'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 13,
    lpSymbol: 'WBNB-DOT LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x92f8a3780D479DCaaeb85D84B9dB2296B96B8603'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'WBNB-DOT LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 14,
    lpSymbol: 'WBNB-DOT LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x7E744494f57511CA6733C83F262b3f674e95F28E'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 15,
    lpSymbol: 'WBNB-XRP LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x3a8db48D7f995D281D894e819C016E8c356757fE'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'WBNB-XRP LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 16,
    lpSymbol: 'WBNB-XRP LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x158522242FD31385F2DdF6762c6d32b337274775'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 17,
    lpSymbol: 'WBNB-BAND LP',
    pool: 'LP',
    lpAddresses: {
      56: '0xDe5bA75b008e93B04Ef1b516f93D1D466e6Dd392'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0x2090b0477E4852f929018c78ba9DD57e3B551d40'
    },
    earn: 'WBNB-BAND LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 18,
    lpSymbol: 'WBNB-BAND LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 19,
    lpSymbol: 'WBNB-UNI LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x680A8a7E94d1EcA0cF651b174727ac5DF36E85c8'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'WBNB-UNI LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 20,
    lpSymbol: 'WBNB-UNI LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x4fcB41f71E31437419ce3Dee4e9c1092cA9cDE1F'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 21,
    lpSymbol: 'WBNB-LINK LP',
    pool: 'LP',
    lpAddresses: {
      56: '0xA37d23D2192a4A61949e3E8339E6B5453FDB2Ae1'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'WBNB-LINK LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 22,
    lpSymbol: 'WBNB-LINK LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x758C6c9731Ee68aD278f8C2F2f27b84aCB092649'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 23,
    lpSymbol: 'USDT-BUSD LP',
    pool: 'LP',
    lpAddresses: {
      56: '0x360DE98FDC44357CED3D0CbD44387160A8f270D4'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'USDT-BUSD LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 24,
    lpSymbol: 'USDT-BUSD LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x360DE98FDC44357CED3D0CbD44387160A8f270D4'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 25,
    lpSymbol: 'VAI-BUSD LP',
    pool: 'LP',
    lpAddresses: {
      56: '0xB6B4417552145C197B060e1CD617e8f1B016c84a'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'VAI-BUSD LP + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 26,
    lpSymbol: 'VAI-BUSD LP Maximizer',
    pool: 'LP',
    lpAddresses: {
      56: '0x3f6919f676d5C7d2A65984dbdf5ae35254be1683'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  },
  {
    pid: 27,
    lpSymbol: 'CAKE-BUSD LP',
    pool: 'LP',
    lpAddresses: {
      56: '0xb8c984498323ee7b5c85fe34419058e0d01884c2'
    },
    tokenSymbol: 'MERL',
    tokenAddresses: {
      56: '0xdc09704Bf547B6639D5911504bC3111Aec8e97Fc'
    },
    earn: 'CAKE + MERL',
    quoteTokenAdresses: wbnb
  }
]

const cakePool = '0xa69ac402a8afe80c4db293baf231242242a131af'
const routerAddress = '0x10ed43c718714eb63d5aa57b78b54704e256024e'

export const getFarmsMerlinLab = async (
  walletAddress: string,
  tokens: Token[],
  priceList: PriceList,
  web3Prop: Web3
): Promise<Stake[]> => {
  try {
    const web3 = web3Prop || new Web3(getNodeUrl(56))
    const myAccount = walletAddress
    const result = await Promise.all<Stake>(
      farms.map(async (item) => {
        try {
          const contract = new web3.eth.Contract(VaultAbi as any, item.lpAddresses[56])
          const stakingToken = await contract.methods.stakingToken().call()
          let rewardToken = await contract.methods.rewardsToken().call()

          const balance = await contract.methods.balanceOf(myAccount).call()
          if (!balance || balance == '0') {
            return null
          }

          // const availableBalanceOf = await balanceOf(myAccount, stakingToken.toLowerCase())

          const deposit = await calculate(
            web3,
            {
              pool: item.pid,
              lpAddress: stakingToken,
              balance: balance.toString(),
              availableBalanceOf: '0'
            },
            routerAddress,
            tokens,
            priceList
          )

          if (rewardToken.toLowerCase() === cakePool) {
            const rewardContract = new web3.eth.Contract(VaultAbi as any, rewardToken)
            rewardToken = await rewardContract.methods.rewardsToken().call()
          }
          const rewardAmount = await contract.methods.earned(myAccount).call()
          const reward = await calculate(
            web3,
            {
              pool: item.pid,
              lpAddress: rewardToken,
              balance: rewardAmount,
              availableBalanceOf: '0'
              // availableBalanceOf: rewardAmount
            },
            routerAddress,
            tokens,
            priceList
          )

          return {
            ...deposit,
            pool: item.pid,
            balance: new BigNumber(balance).dividedBy(1e18).toString(),
            lpAddress: stakingToken,
            availableBalanceOf: '0',
            // availableBalanceOf: new BigNumber(availableBalanceOf).dividedBy(1e18).toString(),
            poolName: item.lpSymbol,
            reward: rewardToken === item.quoteTokenAdresses['56'] ? +reward.balance : 0,
            rewardSymbol: item.earn,
            rewardUSD: reward.worth,
            contractAddress: '0x0c5d20266babc524eedf8719321629e5dfa1a4d9',
            routerAddress: '0x10ed43c718714eb63d5aa57b78b54704e256024e',
            pendingReward: 'pending',
            showReward: true,
            rewardAddress: '0xda360309c59cb8c434b28a91b823344a96444278',
            stake: true,
            unstake: true,
            removeLiquidity: 'NORMAL',
            harvest: true,
            supportIL: true,
            supportROI: false
          }
        } catch (e) {
          return null
        }
      })
    )
    return result
  } catch (e) {
    return null
  }
}

const findFarmByID = (pool: number) => farms.find((i) => i.pid === pool)

export const stakeMerlinLab: StakeFunc = async ({ web3, pool, walletAddress, amount }) => {
  try {
    const farm = findFarmByID(pool)
    const ct = new web3.eth.Contract(VaultAbi as any, farm.lpAddresses[56])
    const contractWithMethod = ct.methods.deposit(new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed())
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
  } catch (e) {
    return false
  }
}

export const unStakeMerlinLab: StakeFunc = async ({ web3, pool, walletAddress, amount }) => {
  try {
    const farm = findFarmByID(pool)
    const ct = new web3.eth.Contract(VaultAbi as any, farm.lpAddresses[56])
    const contractWithMethod = ct.methods[pool >= 2 ? 'withdrawUnderlying' : 'withdraw'](
      new BigNumber(amount).times(new BigNumber(10).pow(18)).toFixed()
    )
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (e) {
    console.log(e)
    return false
  }
}

export const getRewardMerlinLab: StakeFunc<Omit<StakeProps, 'amount'>> = async ({ pool, walletAddress, web3 }) => {
  try {
    const farm = findFarmByID(pool)
    const ct = new web3.eth.Contract(VaultAbi as any, farm.lpAddresses[56])
    const contractWithMethod = ct.methods.getReward()
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (e) {
    console.log(e)
    return false
  }
}
